export const AUTOPLAY_DELAY = 5000;
export const CAROUSEL_SCROLL_DURATION = 420;

export function easeOutCubic(progress) {
  return 1 - ((1 - progress) ** 3);
}

export function nextCarouselIndex(currentIndex, slideCount, direction) {
  if (slideCount < 1) return 0;
  return (currentIndex + direction + slideCount) % slideCount;
}

export function nearestCarouselIndex(scrollLeft, offsets) {
  if (!offsets.length) return 0;

  return offsets.reduce((nearest, offset, index) => (
    Math.abs(offset - scrollLeft) < Math.abs(offsets[nearest] - scrollLeft)
      ? index
      : nearest
  ), 0);
}

export function shouldAutoplay({
  mobile,
  reducedMotion,
  intersecting,
  documentVisible,
  focused,
  slideCount,
}) {
  return (
    mobile &&
    !reducedMotion &&
    intersecting &&
    documentVisible &&
    !focused &&
    slideCount > 1
  );
}

export class PopularProductsCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-spark-carousel-track]');
    this.slides = [...root.querySelectorAll('.spark-home-v2__product-card')];
    this.previous = root.querySelector('[data-spark-carousel-prev]');
    this.next = root.querySelector('[data-spark-carousel-next]');
    this.status = root.querySelector('[data-spark-carousel-status]');
    this.mobileQuery = window.matchMedia('(max-width: 760px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.intersecting = true;
    this.focused = false;
    this.timer = 0;
    this.scrollTimer = 0;
    this.animationFrame = 0;
    this.observer = null;
  }

  init() {
    if (!this.track || !this.previous || !this.next || !this.status) return;

    const hasMultipleSlides = this.slides.length > 1;
    this.previous.hidden = !hasMultipleSlides;
    this.next.hidden = !hasMultipleSlides;
    this.previous.disabled = !hasMultipleSlides;
    this.next.disabled = !hasMultipleSlides;
    this.updateStatus(0);
    if (!hasMultipleSlides) return;

    this.previous.addEventListener('click', () => this.move(-1));
    this.next.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.track.addEventListener('pointerdown', () => {
      this.cancelNavigationAnimation();
      this.stopAutoplay();
    }, { passive: true });
    this.track.addEventListener('pointerup', () => this.restartAutoplay(), { passive: true });

    this.root.addEventListener('focusin', () => this.updateFocusState());
    this.root.addEventListener('focusout', () => {
      window.setTimeout(() => this.updateFocusState(), 0);
    });

    document.addEventListener('visibilitychange', () => this.restartAutoplay());
    this.mobileQuery.addEventListener('change', () => this.handleResponsiveState());
    this.motionQuery.addEventListener('change', () => this.restartAutoplay());

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.intersecting = entry.isIntersecting;
          this.restartAutoplay();
        },
        { threshold: 0.5 },
      );
      this.observer.observe(this.root);
    }

    this.handleResponsiveState();
  }

  activeIndex() {
    if (!this.track) return 0;
    return nearestCarouselIndex(this.track.scrollLeft, this.slideOffsets());
  }

  slideOffsets() {
    const trackLeft = this.track.getBoundingClientRect().left;

    return this.slides.map((slide) => (
      slide.getBoundingClientRect().left - trackLeft + this.track.scrollLeft
    ));
  }

  move(direction, manual = true) {
    const index = nextCarouselIndex(this.activeIndex(), this.slides.length, direction);
    const target = this.slideOffsets()[index] ?? 0;
    if (this.motionQuery.matches) {
      this.cancelNavigationAnimation();
      this.track.scrollLeft = target;
    } else {
      this.animateScrollTo(target);
    }
    this.updateStatus(index);
    if (manual) this.restartAutoplay();
  }

  cancelNavigationAnimation() {
    window.cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
  }

  animateScrollTo(target) {
    this.cancelNavigationAnimation();

    const start = this.track.scrollLeft;
    const distance = target - start;
    const startedAt = window.performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / CAROUSEL_SCROLL_DURATION);
      this.track.scrollLeft = start + (distance * easeOutCubic(progress));

      if (progress < 1) {
        this.animationFrame = window.requestAnimationFrame(step);
      } else {
        this.animationFrame = 0;
        this.track.scrollLeft = target;
      }
    };

    this.animationFrame = window.requestAnimationFrame(step);
  }

  updateStatus(index = this.activeIndex()) {
    this.status.textContent = `Product ${index + 1} of ${this.slides.length}`;
  }

  updateFocusState() {
    this.focused = Boolean(this.root.querySelector(':focus-visible'));
    this.restartAutoplay();
  }

  autoplayState() {
    return {
      mobile: this.mobileQuery.matches,
      reducedMotion: this.motionQuery.matches,
      intersecting: this.intersecting,
      documentVisible: document.visibilityState === 'visible',
      focused: this.focused,
      slideCount: this.slides.length,
    };
  }

  stopAutoplay() {
    window.clearTimeout(this.timer);
    this.timer = 0;
  }

  restartAutoplay() {
    this.stopAutoplay();
    if (!shouldAutoplay(this.autoplayState())) return;

    this.timer = window.setTimeout(() => {
      this.move(1, false);
      this.restartAutoplay();
    }, AUTOPLAY_DELAY);
  }

  handleScroll() {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      this.updateStatus();
      this.restartAutoplay();
    }, 120);
  }

  handleResponsiveState() {
    if (!this.mobileQuery.matches) {
      this.cancelNavigationAnimation();
      this.stopAutoplay();
      this.track.scrollLeft = 0;
      this.updateStatus(0);
      return;
    }

    this.updateStatus();
    this.restartAutoplay();
  }
}

export class DealsDisclosure {
  constructor(root) {
    this.root = root;
    this.button = root.querySelector('[data-spark-deals-toggle]');
    this.collapsedLabel = this.button?.dataset.collapsedLabel || 'View all deals';
    this.expanded = false;
  }

  init() {
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.root.classList.toggle('is-expanded', this.expanded);
      this.button.setAttribute('aria-expanded', String(this.expanded));
      this.button.textContent = this.expanded ? 'Show fewer deals' : this.collapsedLabel;
    });
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-spark-popular-carousel]').forEach((root) => {
    new PopularProductsCarousel(root).init();
  });

  document.querySelectorAll('[data-spark-deals]').forEach((root) => {
    new DealsDisclosure(root).init();
  });
}
